import React, { MutableRefObject, useRef } from 'react';
import cn from 'classnames';
import { useClient, useViewParagraphCount } from 'src/hooks';
import { CONTENT_SUB_TYPE } from 'src/constants';
import { RouterLink } from 'src/components/links';
import BodyBlock from './BodyBlock';
import LiveBodyBlock from './LiveBodyBlock';
import styles from '../style.module.scss';
import { Element } from 'src/components';
import ContentSocial from '../../ContentSocial';
export interface ITextProps {
    type: string;
    content: string;
    subType?: string;
    index: number;
    title: string;
    contentId: number;
    shareUrl: string;
}

const hasSubType = (typeStr = '', type: string) => {
    return typeStr.split(' ').includes(type);
};

export default React.memo(function ContentText(props: ITextProps) {
    const {
        content,
        subType = '',
        type,
        index,
        contentId,
        title,
        shareUrl,
    } = props;
    const { isClient } = useClient();

    const eleRef: MutableRefObject<HTMLImageElement | null> =
        useRef<HTMLImageElement>(null);
    useViewParagraphCount({ index, eleRef, type, contentId });

    if (hasSubType(subType, CONTENT_SUB_TYPE.BODY_BLOCK)) {
        return <BodyBlock {...(props as any)} />;
    } else if (hasSubType(subType, CONTENT_SUB_TYPE.LIVE_BODY_BLOCK)) {
        return <LiveBodyBlock {...(props as any)} />;
    }

    function splitByTagLink(html: string) {
        const regex = /<a\s+class="tag-link"[^>]*>[\s\S]*?<\/a>/;

        const match = html.match(regex);

        if (!match) {
            return { before: html, aTag: '', after: '' };
        }

        const aTag = match[0];
        const index = match.index || 0;

        return {
            before: html.slice(0, index),
            aTag,
            after: html.slice(index + aTag.length),
        };
    }
    if (subType === CONTENT_SUB_TYPE.AUTHOR) {
        return (
            <Element
                className={cn(
                    'flex justify-between gap-[24px]',
                    type,
                    subType,
                )}
            >
                <ContentSocial
                    id={index}
                    contentId={contentId}
                    title={title}
                    url={shareUrl}
                />
                <p
                    ref={eleRef}
                    className={cn(type, subType, 'text-right', '!mb-0 ml-auto')}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            </Element>
        );
    }

    const { before, aTag, after } = splitByTagLink(content);
    if (aTag && isClient) {
        return (
            <p
                ref={eleRef}
                className={cn(styles[type], styles[subType], {
                    ['image-caption text-[#666] text-center text-[14px] leading-[1.5] mt-[-8px]']:
                        subType === CONTENT_SUB_TYPE.IMAGE_CAPTION,
                    ['[&+.image-wrapper]:mt-[32px]']:
                        subType != CONTENT_SUB_TYPE.IMAGE_CAPTION &&
                        subType != CONTENT_SUB_TYPE.AUTHOR,
                })}
            >
                <span dangerouslySetInnerHTML={{ __html: before }} />
                {aTag && <TagLink content={aTag} />}
                <span dangerouslySetInnerHTML={{ __html: after }} />
            </p>
        );
    }

    return (
        <p
            ref={eleRef}
            className={cn(styles[type], styles[subType], {
                ['image-caption text-[#666] text-center text-[14px] leading-[1.5] mt-[-8px]']:
                    subType === CONTENT_SUB_TYPE.IMAGE_CAPTION,
                ['[&+.image-wrapper]:mt-[32px]']:
                    subType != CONTENT_SUB_TYPE.IMAGE_CAPTION &&
                    subType != CONTENT_SUB_TYPE.AUTHOR,
            })}
            dangerouslySetInnerHTML={{ __html: content }}
        />
    );
});

const TagLink = ({ content }: { content: string }) => {
    const regex =
        /<a\s+class="tag-link"[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/;
    const match = content.match(regex);

    if (!match) {
        return null;
    }

    const href = match[1];
    const text = match[2];

    return (
        <RouterLink className="tag-link" to={href}>
            {text}
        </RouterLink>
    );
};
